// PlatformTree — port of design/v3/PlatformTree.jsx, simplified.
// Phase 6.A: groups owner's stores by platform (from /v1/stores). The Phase 5a
// `/v1/analytics/platforms` endpoint is used elsewhere (PlatformPanel).

import { useQuery } from '@tanstack/react-query';
import { storesControllerList } from '@dvvb/sdk';
import { unwrap } from '../../lib/envelope';
import {
  platformColor,
  platformName,
} from '../../lib/format';

export type Selection =
  | { level: 'all' }
  | { level: 'platform'; platformId: string }
  | { level: 'store'; storeId: string; platformId?: string };

interface StoreRow {
  id: string;
  name: string;
  platformId?: string | null;
  platform?: string | null;
}

interface StoresEnvelope {
  items: StoreRow[];
  count: number;
  limit: number;
  cursor: string | null;
  has_more: boolean;
}

interface Props {
  selection: Selection;
  onSelect: (s: Selection) => void;
}

export function PlatformTree({ selection, onSelect }: Props) {
  // Phase 6.B-3-redo hotfix — settings/stores·settings/recipes 와 동일한 queryKey
  // `['stores','list']` 를 공유하므로 같은 envelope 형태로 cache 해야 cross-route
  // navigation 시 cache hit 으로 인한 stale-state 회귀가 발생하지 않는다.
  const q = useQuery({
    queryKey: ['stores', 'list'],
    queryFn: async () => {
      const r = await storesControllerList({
        query: { limit: '500' } as never,
      });
      return unwrap<StoresEnvelope>(r);
    },
  });

  const stores = q.data?.items ?? [];
  // Group by platformId
  const grouped: Record<string, StoreRow[]> = {};
  for (const s of stores) {
    const k = (s.platformId ?? s.platform ?? 'unknown') as string;
    if (!grouped[k]) grouped[k] = [];
    grouped[k]!.push(s);
  }
  const platformIds = Object.keys(grouped);

  return (
    <aside
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-weak)',
        borderRadius: 'var(--radius-lg)',
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 8px 10px',
          borderBottom: '1px solid var(--border-weak)',
          marginBottom: 8,
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--fg-3)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          플랫폼 · 상점
        </span>
      </div>

      <Row
        active={selection.level === 'all'}
        onClick={() => onSelect({ level: 'all' })}
        bold
        label="전체"
        sub={`${stores.length}개 상점`}
        icon={
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              background:
                'linear-gradient(135deg, var(--platform-baemin), var(--platform-coupang) 50%, var(--platform-yogiyo))',
              flexShrink: 0,
            }}
          />
        }
      />

      <div style={{ height: 8 }} />

      {q.isLoading && (
        <div
          style={{
            padding: '14px 8px',
            fontSize: 12,
            color: 'var(--fg-3)',
            textAlign: 'center',
          }}
        >
          매장 불러오는 중…
        </div>
      )}
      {q.isError && (
        <div
          style={{
            padding: '10px 8px',
            fontSize: 12,
            color: 'var(--danger)',
          }}
        >
          매장 목록을 불러오지 못했습니다.
        </div>
      )}
      {q.data && stores.length === 0 && (
        <div
          style={{
            padding: '14px 8px',
            fontSize: 12,
            color: 'var(--fg-3)',
            textAlign: 'center',
            fontStyle: 'italic',
          }}
        >
          등록된 매장이 없습니다.
        </div>
      )}
      {platformIds.map((pid) => {
        const ps = grouped[pid] ?? [];
        const platActive =
          selection.level === 'platform' && selection.platformId === pid;
        return (
          <div key={pid}>
            <Row
              active={platActive}
              onClick={() => onSelect({ level: 'platform', platformId: pid })}
              bold
              label={platformName(pid)}
              sub={`${ps.length}개 상점`}
              icon={
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 999,
                    background: platformColor(pid),
                    flexShrink: 0,
                  }}
                />
              }
            />
            <div style={{ marginLeft: 22 }}>
              {ps.map((s) => {
                const active =
                  selection.level === 'store' && selection.storeId === s.id;
                return (
                  <Row
                    key={s.id}
                    active={active}
                    onClick={() =>
                      onSelect({
                        level: 'store',
                        storeId: s.id,
                        platformId: pid,
                      })
                    }
                    label={s.name}
                    icon={
                      <span
                        style={{
                          width: 5,
                          height: 5,
                          borderRadius: 999,
                          background: platformColor(pid),
                          flexShrink: 0,
                          marginLeft: 3,
                        }}
                      />
                    }
                  />
                );
              })}
            </div>
            <div style={{ height: 4 }} />
          </div>
        );
      })}
    </aside>
  );
}

interface RowProps {
  label: string;
  sub?: string;
  icon?: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
  bold?: boolean;
}

function Row({ label, sub, icon, active, onClick, bold }: RowProps) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        alignItems: 'center',
        gap: 8,
        padding: '7px 10px',
        borderRadius: 6,
        cursor: 'pointer',
        background: active ? 'var(--primary-bg)' : 'transparent',
        border: active
          ? '1px solid var(--primary-border)'
          : '1px solid transparent',
        transition: 'background 120ms',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          minWidth: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: bold ? 600 : 500,
            color: active ? 'var(--primary-strong)' : 'var(--fg-1)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </div>
        {sub && (
          <div
            style={{
              fontSize: 11,
              color: 'var(--fg-3)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontVariantNumeric: 'tabular-nums',
              marginTop: 1,
            }}
          >
            {sub}
          </div>
        )}
      </div>
    </div>
  );
}
